/**
 * @file StickyMobileCta.jsx
 * @description One reusable sticky bottom call-to-action for long public pages on phones.
 *   A slim bar fixed to the bottom with a single brand-indigo button. It only exists on
 *   narrow viewports (under 760px), appears once the visitor has scrolled past the hero
 *   (about 500px), and gets out of the way whenever the footer, or any section a page marks
 *   `data-sticky-cta-hide` (today only the agency pricing section), is on screen, so it never covers them.
 *   Pages no longer end on a closing CTA band (one call to action per page), so the footer is the usual hide target.
 *
 *   Bottom clearance: the component renders an in-flow spacer (bar height + safe-area-inset-bottom) as
 *   the last child of the page it is placed in, so it sits inside the page's own scroll container
 *   (the App-level padding does not reach those inner scrollers). The bar itself respects
 *   safe-area-inset-bottom, and while it is visible it publishes that to the header
 *   (lib/stickyCtaSignal) so the header CTA steps aside on phones.
 *
 *   Used on: tracker landing pages, city job pages, agency pages. NOT on the home page,
 *   the guides, or any logged-in view.
 * @module components/StickyMobileCta
 */
import { useEffect, useState } from "react";
import { T } from "../theme";
import { setStickyCtaVisible } from "../lib/stickyCtaSignal";

export const STICKY_CTA_BREAKPOINT = 760;
export const STICKY_CTA_SCROLL_PX = 500;
/** In-flow clearance at the page bottom: bar height (10 + 46 + 10, rounded up) + the iOS home-indicator inset. */
export const STICKY_CTA_CLEARANCE = "calc(68px + env(safe-area-inset-bottom, 0px))";
/** Elements that, when on screen, hide the bar: any section marked data-sticky-cta-hide (agency pricing) + the footer. */
export const STICKY_CTA_HIDE_SELECTOR = "[data-sticky-cta-hide], footer";

const isNarrow = () => typeof window !== "undefined" && window.innerWidth < STICKY_CTA_BREAKPOINT;

function scrolledPx(target) {
  const winY = typeof window !== "undefined" ? (window.scrollY || document.documentElement.scrollTop || 0) : 0;
  const elY = target && target !== document && target !== window && typeof target.scrollTop === "number" ? target.scrollTop : 0;
  return Math.max(winY, elY);
}

function hideTargetOnScreen() {
  if (typeof document === "undefined") return false;
  const vh = window.innerHeight || document.documentElement.clientHeight || 0;
  return Array.from(document.querySelectorAll(STICKY_CTA_HIDE_SELECTOR)).some((el) => {
    const r = el.getBoundingClientRect();
    return r.height > 0 && r.top < vh && r.bottom > 0;
  });
}

/**
 * @param {object} props
 * @param {string} props.label Button text (already translated).
 * @param {() => void} props.onClick Same handler (tracking, intent, navigation) as the page's hero CTA.
 * @param {string} [props.ctaId] Identifier for tests/analytics hooks.
 */
export default function StickyMobileCta({ label, onClick, ctaId }) {
  const [narrow, setNarrow] = useState(isNarrow);
  const [pastHero, setPastHero] = useState(false);
  const [blocked, setBlocked] = useState(false);

  useEffect(() => {
    const evaluate = (target) => {
      const n = isNarrow();
      setNarrow(n);
      setPastHero(n && scrolledPx(target) > STICKY_CTA_SCROLL_PX);
      setBlocked(n && hideTargetOnScreen());
    };
    const onScroll = (e) => evaluate(e && e.target);
    const onResize = () => evaluate();
    evaluate();
    // Capture phase so a scrolling inner container (not just the window) also drives the bar.
    window.addEventListener("scroll", onScroll, { passive: true, capture: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll, { capture: true });
      window.removeEventListener("resize", onResize);
    };
  }, []);

  const shown = narrow && pastHero && !blocked;
  // Tell the header (it hides its own CTA on phones while this bar is up) and clean up on unmount.
  useEffect(() => {
    setStickyCtaVisible(shown);
    return () => setStickyCtaVisible(false);
  }, [shown]);

  if (!narrow) return null;

  // The spacer lives INSIDE the page's own scroll container (this component renders as the last child of
  // each page), so the end of the page is never covered by the fixed bar. It continues the footer's navy.
  const spacer = <div aria-hidden="true" data-testid="sticky-cta-spacer" style={{ height: STICKY_CTA_CLEARANCE, background: "#171b36" }} />;
  if (!shown) return spacer;

  return (
    <>
    {spacer}
    <div
      data-testid="sticky-mobile-cta"
      data-cta-id={ctaId}
      style={{
        position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 1800,
        background: "rgba(255,255,255,0.96)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
        borderTop: `1px solid ${T.border}`, boxShadow: "0 -2px 14px rgba(15,23,42,0.10)",
        padding: "10px 16px", paddingBottom: "calc(10px + env(safe-area-inset-bottom, 0px))",
        boxSizing: "border-box",
      }}
    >
      <button
        type="button"
        onClick={onClick}
        style={{
          display: "flex", alignItems: "center", justifyContent: "center", width: "100%", minHeight: 46,
          padding: "12px 20px", border: "none", borderRadius: 12, cursor: "pointer",
          background: T.gradientIndigo, color: "#fff", fontSize: 15, fontWeight: 800,
          fontFamily: T.fontBody, boxShadow: "0 4px 16px rgba(79,70,229,0.35)",
        }}
      >
        {label}
      </button>
    </div>
    </>
  );
}
